import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/base/accordion';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/base/dropdown-menu';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';
import { HeaderInfoRow } from '@/ui/HeaderInfoRow';
import { TimelineBadgeStepper } from '@/ui/TimelineBadgeStepper';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Banknote,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Download,
  ExternalLink,
  FileOutput,
  FileSpreadsheet,
  List,
  Lock,
  MoreHorizontal,
  PlayCircle,
  RotateCcw,
  Scale,
  Search,
  ShieldAlert,
  ShieldCheck,
  Unlock,
  UserCheck,
  Upload,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';

import { AcompanhamentoMensalTable } from '../apuracao/AcompanhamentoMensalTable';
import { ApontamentoGridPage } from '../apontamentos/ApontamentoGridPage';
import { DemonstrativosPage } from '../DemonstrativosPage';
import { PipelineFinanceiro } from '../PipelineFinanceiro';
import { useDres } from '../hooks/useDres';
import { exportarCsv } from '@/ui/exportCsv';
import {
  useCicloFinanceiroPipeline,
  useComposicaoValores,
  useFechamentoV2Resumo,
  useGovernancaComposicao,
  useImportarPlanilhaAteste,
  useLotesAteste,
  useTransicaoGovernancaComposicao,
} from '../hooks/useFechamentoV2';
import type {
  ComposicaoValoresLinha,
  GovernancaComposicaoEstado,
  LoteAtesteImportado,
  StatusGovernancaComposicao,
} from '../types';
import { construirEtapasTimeline } from './etapas';
import { NaturezaToggle } from './NaturezaToggle';
import { type PersonaFechamento, usePersonaFechamento } from './persona';
import { useFechamentoBasePath } from './useFechamentoBasePath';
import { cn } from '@/nucleo/utilitarios';

const ABAS = [
  'apontamento',
  'composicao-valores',
  'conciliacao-atestes',
  'demonstrativos',
  'pos-fechamento',
  'documentos',
] as const;
type AbaId = (typeof ABAS)[number];

/**
 * Abas por persona, iguais ao protótipo (`DRE_ALLOWED_TABS`,
 * `SME_ALLOWED_TABS`): a DRE vê o que apontou e o demonstrativo; a SME ganha o
 * acompanhamento do ciclo; Ateste, Conciliação e Pagamento são trabalho do DTP.
 * Aba fora do recorte não é renderizada (CLAUDE.md, regra de interface 7).
 */
const ABAS_POR_PERSONA: Record<PersonaFechamento, readonly AbaId[]> = {
  DTP: ABAS,
  SME: ['apontamento', 'demonstrativos', 'documentos'],
  DRE: ['apontamento', 'demonstrativos'],
};

const ABA_INFO: Record<AbaId, { icon: typeof ClipboardCheck; dica: string }> = {
  apontamento: {
    icon: ClipboardCheck,
    dica: 'Grade de apontamentos das DREs por OS — base de cálculo da medição (faltas, ajustes, atividades contínuas).',
  },
  'composicao-valores': {
    icon: Scale,
    dica: 'Ateste do mês: composição do valor final (base + revisões (±) + ajustes/multas (±) − descontos), com drill-down por credenciado → OS → dia/modalidade e aprovação em 2 níveis (Operador DTP → Gestor DTP).',
  },
  'conciliacao-atestes': {
    icon: FileSpreadsheet,
    dica: 'Importe planilhas (PF/PJ) enviadas por terceiros para pré-visualizar como os apontamentos serão interpretados — inconsistências são destacadas por tipo antes da conciliação.',
  },
  demonstrativos: {
    icon: FileOutput,
    dica: 'Demonstrativos formais por credenciado (PF/PJ/Coop) — espelho do cálculo enviado para validação.',
  },
  'pos-fechamento': {
    icon: ShieldCheck,
    dica: 'Operação por lote: Exportação SOF (gerar/enviar/confirmar/anexar protocolo), Pagamento & Conciliação e Selamento Contábil.',
  },
  documentos: {
    icon: Activity,
    dica: 'Painel do ciclo mensal: status por etapa (Apontamento → SME → Empenho → Liquidação → SOF → Pagamento) para a competência.',
  },
};

const ROTULO_ABA: Record<AbaId, string> = {
  apontamento: 'Apontamentos',
  'composicao-valores': 'Ateste',
  'conciliacao-atestes': 'Conciliação de atestes',
  demonstrativos: 'Demonstrativos',
  'pos-fechamento': 'Pagamento & Selamento',
  documentos: 'Acompanhamento',
};

const ROTULO_GOVERNANCA: Record<
  StatusGovernancaComposicao,
  { label: string; tone: 'rascunho' | 'pendente' | 'aprovado' }
> = {
  EM_CONFERENCIA: { label: 'Em conferência', tone: 'rascunho' },
  CONGELADO: { label: 'Congelado', tone: 'pendente' },
  AGUARDANDO_GESTOR: { label: 'Aguardando Gestor DTP', tone: 'pendente' },
  ATESTADO: { label: 'Atestado', tone: 'aprovado' },
};

/**
 * O que a DRE recebe no lugar do conteúdo de Apontamentos e Demonstrativos. As
 * duas abas continuam na barra dela (a matriz de persona é a do protótipo), mas
 * o dado não: no protótipo o banco recorta pela diretoria de quem olha, e aqui
 * `UsuarioAutenticado` (`GET /eu`) não tem campo de DRE, então não há como saber
 * quais linhas são da dela. Mostrar as de todas seria pior que não mostrar
 * nenhuma — mesma saída de Mensagens, Tratativas e do Ciclo mensal
 * (CLAUDE.md, exceção do portal SME, regra da persona DRE).
 */
function SemRecorteDaDre({ assunto }: { assunto: string }) {
  return (
    <EmptyState
      icon={Building2}
      title="O que é da sua diretoria ainda não está disponível"
      description={`Mostrar em ${assunto} só o que é da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá o conteúdo não é exibido: mostrar o de todas as diretorias seria pior do que não mostrar nenhum.`}
    />
  );
}

function parseFechamentoV2Id(id: string): { mesReferencia: string; natureza: 'PF' | 'PJ' } {
  const [mes, natureza] = id.split('__');
  return { mesReferencia: mes ?? '', natureza: natureza === 'PJ' ? 'PJ' : 'PF' };
}

/** Mês atual + 12 anteriores, mais recente primeiro — mesma janela do seletor
 * de competência do protótipo. */
function opcoesDeMes(): { value: string; label: string }[] {
  const hoje = new Date();
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return { value, label };
  });
}

/**
 * `/operacao/financeiro/fechamento-mensal/:id` (FIN-023) — detalhe do
 * fechamento mensal de uma competência/natureza, com as 6 abas do protótipo.
 * `:id` é composto (`mesReferencia__natureza`, ex. `2026-08-01__PF`).
 */
export function FechamentoMensalV2DetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const base = useFechamentoBasePath();
  const [parametros, definirParametros] = useSearchParams();
  const { mesReferencia, natureza } = parseFechamentoV2Id(id);
  const resumo = useFechamentoV2Resumo(mesReferencia, natureza);
  const [historicoAberto, definirHistoricoAberto] = useState(false);
  const [processando, definirProcessando] = useState(false);
  const [reforcoAberto, definirReforcoAberto] = useState(false);
  const persona = usePersonaFechamento();
  const abasVisiveis = ABAS_POR_PERSONA[persona];
  const ehDtp = persona === 'DTP';

  const abaPedida = parametros.get('aba') as AbaId | null;
  const abaForaDoRecorte = abaPedida !== null && !abasVisiveis.includes(abaPedida);
  const aba: AbaId = abaPedida && !abaForaDoRecorte ? abaPedida : 'apontamento';

  // Link antigo ou compartilhado por outra persona: avisa e cai em
  // Apontamentos, como o protótipo, em vez de abrir uma aba vazia.
  useEffect(() => {
    if (!abaForaDoRecorte) return;
    toast('Aba indisponível para o seu perfil', {
      description: 'Mostrando Apontamentos desta competência.',
    });
    definirParametros(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.set('aba', 'apontamento');
        return proximo;
      },
      { replace: true },
    );
  }, [abaForaDoRecorte, definirParametros]);
  function mudarAba(novaAba: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('aba', novaAba);
      return proximo;
    });
  }

  function trocarNatureza(novaNatureza: 'PF' | 'PJ') {
    navigate(
      `${base}/fechamento-mensal/${mesReferencia}__${novaNatureza}?${parametros.toString()}`,
    );
  }

  function trocarMes(novoMesAno: string) {
    if (novoMesAno === mesReferencia.slice(0, 7)) return;
    navigate(`${base}/fechamento-mensal/${novoMesAno}-01__${natureza}`, {
      replace: true,
    });
  }

  const r = resumo.data;
  const percentualOs = r && r.totalOs > 0 ? Math.round((r.totalOsPagas / r.totalOs) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`${base}/fechamento-mensal?view=lista`)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Voltar
        </Button>
        <Button variant="ghost" size="sm" className="text-muted-foreground" asChild>
          <Link to={`${base}/fechamento-mensal?view=lista`}>
            <List className="mr-1 h-4 w-4" aria-hidden="true" />
            Ver lista de competências
          </Link>
        </Button>
        {ehDtp && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground" asChild>
                <Link
                  to={`/operacao/financeiro/empenho-liquidacao?mes=${mesReferencia.slice(0, 7)}`}
                >
                  <ExternalLink className="mr-1 h-4 w-4" aria-hidden="true" />
                  Ver no ciclo (multi-mês)
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-xs">
              Abre a fila Empenho → Liquidação → Autorização de todas as medições, já filtrada por
              esta competência. Útil para comparar com meses anteriores.
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">Fechamento de</h1>
            <Select value={mesReferencia.slice(0, 7)} onValueChange={trocarMes}>
              <SelectTrigger
                className="h-10 w-auto min-w-[200px] px-3 text-2xl font-bold capitalize"
                aria-label="Selecionar competência (mês/ano)"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opcoesDeMes().map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="capitalize">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {r && (
              <TimelineBadgeStepper
                steps={construirEtapasTimeline(
                  r.etapa,
                  r.responsavel,
                  r.proximoPasso,
                  r.atrasado,
                  r.diasParaPrazo,
                )}
              />
            )}
          </div>

          {r && (
            <div className="mt-1 flex flex-col gap-2">
              <HeaderInfoRow
                icon={Scale}
                label="Resultado"
                description="Quanto da competência já virou pagamento — em OS e em valor (pago / total)."
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                  <span className="tabular-nums">
                    <strong className="text-foreground">{r.totalOsPagas}</strong>
                    <span className="text-muted-foreground">
                      {' '}
                      de {r.totalOs} OS pagas ({percentualOs}%)
                    </span>
                  </span>
                  {parseFloat(r.valorCalculado) > 0 && (
                    <>
                      <span className="text-muted-foreground" aria-hidden="true">
                        ·
                      </span>
                      <span className="tabular-nums">
                        <MoneyDisplay value={r.valorPago} />
                        <span className="text-muted-foreground"> de </span>
                        <MoneyDisplay value={r.valorCalculado} />
                        <span className="text-muted-foreground"> pagos</span>
                      </span>
                    </>
                  )}
                  {r.etapa !== 'concluido' && r.atrasado && (
                    <Badge variant="destructive">{Math.abs(r.diasParaPrazo)}d em atraso</Badge>
                  )}
                </div>
              </HeaderInfoRow>

              <HeaderInfoRow
                icon={Activity}
                label="Operação"
                description="Distribuição das OS por estado interno do fechamento (rascunho, validação, aprovação, pagamento, bloqueios)."
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{r.contagemRascunho} Rascunhos</Badge>
                  <Badge variant="outline">{r.contagemProcessada} Em validação</Badge>
                  <Badge variant="outline">{r.contagemAprovada} Aprovada</Badge>
                  {r.bloqueiosTotal > 0 && (
                    <Badge variant="destructive">{r.bloqueiosTotal} Bloqueios</Badge>
                  )}
                </div>
              </HeaderInfoRow>

              {persona !== 'DRE' && (
                <HeaderInfoRow
                  icon={Banknote}
                  label="Ciclo SOF"
                  description="Pipeline orçamentário-financeiro no SOF: cada etapa só avança quando a anterior está concluída."
                >
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {r.cicloEmpenho} Empenho{' '}
                      <ArrowRight className="mx-1 inline h-3 w-3" aria-hidden="true" />
                      {r.cicloLiquidacao} Liquidação{' '}
                      <ArrowRight className="mx-1 inline h-3 w-3" aria-hidden="true" />
                      {r.cicloAutorizacao} Autorização
                    </span>
                    {abasVisiveis.includes('pos-fechamento') && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => mudarAba('pos-fechamento')}
                      >
                        Ver detalhe <ArrowRight className="ml-1 h-3 w-3" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </HeaderInfoRow>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <NaturezaToggle value={natureza} onChange={trocarNatureza} />
          {persona !== 'DRE' && (
            <div className="flex items-center gap-1">
              {r && r.medicoesValidaveis > 0 && abasVisiveis.includes('composicao-valores') && (
                <Button onClick={() => mudarAba('composicao-valores')}>
                  Aprovar {r.medicoesValidaveis} medições validáveis{' '}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Ações da competência">
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Ações da competência</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => definirHistoricoAberto(true)}>
                    <Clock className="mr-2 h-4 w-4" aria-hidden="true" />
                    Ver histórico
                  </DropdownMenuItem>
                  {ehDtp && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => definirProcessando(true)}>
                        <PlayCircle className="mr-2 h-4 w-4 text-primary" aria-hidden="true" />
                        Processar competência{r ? ` (${r.totalOs} OS)` : ''}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => definirReforcoAberto(true)}>
                    <ShieldAlert className="mr-2 h-4 w-4" aria-hidden="true" />
                    Solicitar reforço
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <Tabs value={aba} onValueChange={mudarAba}>
        <TabsList className="w-max min-w-full gap-1 rounded-2xl border-border/40 bg-muted/50 p-1 backdrop-blur-xs">
          {abasVisiveis.map((chave) => {
            const { icon: Icone, dica } = ABA_INFO[chave];
            const ativa = aba === chave;
            return (
              <Tooltip key={chave}>
                <TooltipTrigger asChild>
                  <TabsTrigger
                    value={chave}
                    className={cn(
                      'gap-2 rounded-xl px-4 py-2 duration-200',
                      ativa && 'bg-card font-semibold shadow-xs',
                    )}
                  >
                    <Icone className="h-4 w-4" aria-hidden="true" />
                    {ROTULO_ABA[chave]}
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  {dica}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TabsList>

        <TabsContent value="apontamento">
          {persona === 'DRE' ? (
            <SemRecorteDaDre assunto="Apontamentos" />
          ) : (
            <ApontamentoGridPage embedded mesReferencia={mesReferencia} natureza={natureza} />
          )}
        </TabsContent>

        {abasVisiveis.includes('composicao-valores') && (
          <TabsContent value="composicao-valores">
            <AbaComposicaoValores
              mes={mesReferencia}
              natureza={natureza}
              onTrocarNatureza={trocarNatureza}
            />
          </TabsContent>
        )}

        {abasVisiveis.includes('conciliacao-atestes') && (
          <TabsContent value="conciliacao-atestes">
            <AbaConciliacaoAtestes mes={mesReferencia} natureza={natureza} />
          </TabsContent>
        )}

        <TabsContent value="demonstrativos">
          {persona === 'DRE' ? (
            <SemRecorteDaDre assunto="Demonstrativos" />
          ) : (
            <DemonstrativosPage embedded mesReferencia={mesReferencia} />
          )}
        </TabsContent>

        {abasVisiveis.includes('pos-fechamento') && (
          <TabsContent value="pos-fechamento">
            <AbaPagamentoSelamento mes={mesReferencia} />
          </TabsContent>
        )}

        {abasVisiveis.includes('documentos') && (
          <TabsContent value="documentos">
            <AcompanhamentoMensalTable mes={mesReferencia} />
          </TabsContent>
        )}
      </Tabs>

      <ResponsiveModal
        open={historicoAberto}
        onOpenChange={definirHistoricoAberto}
        title="Histórico da competência"
        description="Trilha de alterações desta competência — etapa futura da migração."
      >
        <EmptyState
          title="Sem histórico detalhado ainda"
          description="Esta trilha entra em uma próxima etapa da migração, junto com o backend do módulo financeiro."
        />
      </ResponsiveModal>

      <ResponsiveModal
        open={processando}
        onOpenChange={definirProcessando}
        title="Processar competência"
        description={
          r ? `Processa as ${r.totalOs} OS recebidas do DTP nesta competência.` : undefined
        }
      >
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => definirProcessando(false)}>
            Cancelar
          </Button>
          <Button onClick={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}>Processar</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        open={reforcoAberto}
        onOpenChange={definirReforcoAberto}
        title="Solicitar reforço"
        description="Pede apoio de outra equipe para agilizar esta competência."
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="reforco-obs">Observação</Label>
            <Input id="reforco-obs" placeholder="Descreva o motivo do pedido de reforço" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirReforcoAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}>Enviar pedido</Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

/** Um credenciado com suas OS agrupadas — nível 1 da tabela de composição
 * (nível 2 é a própria `ComposicaoValoresLinha`, uma por OS). */
interface CredenciadoComposicaoResumo {
  credenciadoId: string;
  credenciadoNome: string;
  oss: ComposicaoValoresLinha[];
  valorBase: number;
  descontoFaltas: number;
  glosaVeiculo: number;
  ajustesPositivos: number;
  ajustesNegativos: number;
  valorFinal: number;
}

function agruparPorCredenciado(linhas: ComposicaoValoresLinha[]): CredenciadoComposicaoResumo[] {
  const mapa = new Map<string, CredenciadoComposicaoResumo>();
  for (const l of linhas) {
    const atual = mapa.get(l.credenciadoId) ?? {
      credenciadoId: l.credenciadoId,
      credenciadoNome: l.credenciadoNome,
      oss: [],
      valorBase: 0,
      descontoFaltas: 0,
      glosaVeiculo: 0,
      ajustesPositivos: 0,
      ajustesNegativos: 0,
      valorFinal: 0,
    };
    atual.oss.push(l);
    atual.valorBase += parseFloat(l.valorBase);
    atual.descontoFaltas += parseFloat(l.descontoFaltas);
    atual.glosaVeiculo += parseFloat(l.glosaVeiculo);
    atual.ajustesPositivos += parseFloat(l.ajustesPositivos);
    atual.ajustesNegativos += parseFloat(l.ajustesNegativos);
    atual.valorFinal += parseFloat(l.valorFinal);
    mapa.set(l.credenciadoId, atual);
  }
  return [...mapa.values()];
}

function CarimboLinha({
  titulo,
  quando,
  quem,
}: {
  titulo: string;
  quando: string | null;
  quem: string | null;
}) {
  return (
    <div className="rounded-md border bg-muted/20 px-2.5 py-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {titulo}
      </div>
      {quando ? (
        <div className="mt-0.5">
          <div className="font-medium">
            {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
              new Date(quando),
            )}
          </div>
          {quem && <div className="truncate text-muted-foreground">{quem}</div>}
        </div>
      ) : (
        <div className="mt-0.5 text-muted-foreground">—</div>
      )}
    </div>
  );
}

const GOVERNANCA_ICONE: Record<StatusGovernancaComposicao, typeof Scale> = {
  EM_CONFERENCIA: Scale,
  CONGELADO: Lock,
  AGUARDANDO_GESTOR: UserCheck,
  ATESTADO: ShieldCheck,
};

function AbaComposicaoValores({
  mes,
  natureza,
  onTrocarNatureza,
}: {
  mes: string;
  natureza: 'PF' | 'PJ';
  onTrocarNatureza: (novaNatureza: 'PF' | 'PJ') => void;
}) {
  const composicao = useComposicaoValores(mes, natureza);
  const governanca = useGovernancaComposicao(mes);
  const transicao = useTransicaoGovernancaComposicao(mes);
  const { data: dres } = useDres();
  const [devolvendo, definirDevolvendo] = useState(false);
  const [motivo, definirMotivo] = useState('');
  const [liberando, definirLiberando] = useState(false);
  const [motivoLiberacao, definirMotivoLiberacao] = useState('');
  const [busca, definirBusca] = useState('');
  const [credenciadoId, definirCredenciadoId] = useState<string | null>(null);
  const [dreId, definirDreId] = useState<string | null>(null);
  const [osId, definirOsId] = useState<string | null>(null);
  const [expandidos, definirExpandidos] = useState<Set<string>>(new Set());

  const g: GovernancaComposicaoEstado = governanca.data ?? {
    status: 'EM_CONFERENCIA',
    congeladoEm: null,
    congeladoPor: null,
    atestadoOperadorEm: null,
    atestadoOperadorPor: null,
    atestadoGestorEm: null,
    atestadoGestorPor: null,
    motivoDevolucao: null,
  };
  const tom = ROTULO_GOVERNANCA[g.status];
  const IconeGovernanca = GOVERNANCA_ICONE[g.status];

  const todasLinhas = useMemo(() => composicao.data ?? [], [composicao.data]);

  const opcoesCredenciado = useMemo(() => {
    const vistos = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const l of todasLinhas) {
      if (vistos.has(l.credenciadoId)) continue;
      vistos.add(l.credenciadoId);
      opts.push({ value: l.credenciadoId, label: l.credenciadoNome });
    }
    return opts;
  }, [todasLinhas]);

  const opcoesOs = useMemo(
    () => todasLinhas.map((l) => ({ value: l.ordemServicoId, label: `OS ${l.numeroOs}` })),
    [todasLinhas],
  );

  const opcoesDre = useMemo(
    () => (dres ?? []).map((d) => ({ value: d.id, label: d.nome ?? d.codigo ?? d.id })),
    [dres],
  );

  const linhasFiltradas = useMemo(() => {
    let base = todasLinhas;
    if (credenciadoId) base = base.filter((l) => l.credenciadoId === credenciadoId);
    if (dreId) base = base.filter((l) => l.dreId === dreId);
    if (osId) base = base.filter((l) => l.ordemServicoId === osId);
    if (busca.trim()) {
      const termo = busca.trim().toLowerCase();
      base = base.filter(
        (l) =>
          l.credenciadoNome.toLowerCase().includes(termo) || String(l.numeroOs).includes(termo),
      );
    }
    return base;
  }, [todasLinhas, credenciadoId, dreId, osId, busca]);

  const credenciados = useMemo(() => agruparPorCredenciado(linhasFiltradas), [linhasFiltradas]);

  const totais = useMemo(
    () =>
      linhasFiltradas.reduce(
        (acc, l) => ({
          valorBase: acc.valorBase + parseFloat(l.valorBase),
          descontoFaltas: acc.descontoFaltas + parseFloat(l.descontoFaltas),
          glosaVeiculo: acc.glosaVeiculo + parseFloat(l.glosaVeiculo),
          ajustesPositivos: acc.ajustesPositivos + parseFloat(l.ajustesPositivos),
          ajustesNegativos: acc.ajustesNegativos + parseFloat(l.ajustesNegativos),
          valorFinal: acc.valorFinal + parseFloat(l.valorFinal),
        }),
        {
          valorBase: 0,
          descontoFaltas: 0,
          glosaVeiculo: 0,
          ajustesPositivos: 0,
          ajustesNegativos: 0,
          valorFinal: 0,
        },
      ),
    [linhasFiltradas],
  );

  const hasFiltros = busca.length > 0 || !!credenciadoId || !!dreId || !!osId;
  const limparFiltros = () => {
    definirBusca('');
    definirCredenciadoId(null);
    definirDreId(null);
    definirOsId(null);
  };

  function alternarExpandido(id: string) {
    definirExpandidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  return (
    <div className="space-y-4">
      <Card
        className={cn(
          'border-l-4',
          tom.tone === 'aprovado'
            ? 'border-l-success'
            : tom.tone === 'pendente'
              ? 'border-l-warning'
              : 'border-l-muted-foreground/40',
        )}
      >
        <CardContent className="space-y-3 px-4 py-4">
          <div className="flex flex-wrap items-start gap-3">
            <IconeGovernanca
              className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <h3 className="text-base font-semibold capitalize">
                  Ateste do mês —{' '}
                  {new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </h3>
                <span className="text-xs text-muted-foreground">
                  · {natureza === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica / Cooperativa'}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusBadge status={tom.tone} label={tom.label} />
                <span className="text-xs text-muted-foreground">
                  Composição de valores do mês: base + revisões (±) + ajustes/multas (±) −
                  descontos. Congele antes do demonstrativo e aprove em 2 níveis.
                </span>
              </div>

              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
                <CarimboLinha titulo="Congelamento" quando={g.congeladoEm} quem={g.congeladoPor} />
                <CarimboLinha
                  titulo="Ateste — Operador DTP"
                  quando={g.atestadoOperadorEm}
                  quem={g.atestadoOperadorPor}
                />
                <CarimboLinha
                  titulo="Aprovação — Gestor DTP"
                  quando={g.atestadoGestorEm}
                  quem={g.atestadoGestorPor}
                />
              </div>

              {g.motivoDevolucao && g.status === 'CONGELADO' && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-warning" aria-hidden="true" />
                  <span>
                    <strong>Devolvido pelo Gestor DTP:</strong> {g.motivoDevolucao}
                  </span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {g.status === 'EM_CONFERENCIA' && (
                <Button
                  size="sm"
                  onClick={() => transicao.mutate({ acao: 'CONGELAR' })}
                  disabled={transicao.isPending}
                >
                  <Lock className="mr-1 h-4 w-4" aria-hidden="true" /> Congelar mês
                </Button>
              )}
              {g.status === 'CONGELADO' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => transicao.mutate({ acao: 'ATESTAR' })}
                    disabled={transicao.isPending}
                  >
                    <UserCheck className="mr-1 h-4 w-4" aria-hidden="true" /> Atestar (Operador DTP)
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => definirLiberando(true)}>
                    <Unlock className="mr-1 h-4 w-4" aria-hidden="true" /> Liberar
                  </Button>
                </>
              )}
              {g.status === 'AGUARDANDO_GESTOR' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => transicao.mutate({ acao: 'APROVAR' })}
                    disabled={transicao.isPending}
                  >
                    <ShieldCheck className="mr-1 h-4 w-4" aria-hidden="true" /> Aprovar (Gestor DTP)
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => definirDevolvendo(true)}>
                    <RotateCcw className="mr-1 h-4 w-4" aria-hidden="true" /> Devolver
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => definirLiberando(true)}>
                    <Unlock className="mr-1 h-4 w-4" aria-hidden="true" /> Liberar
                  </Button>
                </>
              )}
              {g.status === 'ATESTADO' && (
                <Button size="sm" variant="outline" onClick={() => definirLiberando(true)}>
                  <Unlock className="mr-1 h-4 w-4" aria-hidden="true" /> Liberar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2.5 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-9 items-center gap-2 rounded-md border bg-muted/40 pl-2 pr-3 text-sm">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Mês
              </span>
              <span className="font-medium capitalize">
                {new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>

            <NaturezaToggle value={natureza} onChange={onTrocarNatureza} />

            <div className="relative min-w-[240px] flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                placeholder="Buscar por credenciado ou nº de OS…"
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
                className="h-9 pl-9"
                aria-label="Buscar credenciado ou OS"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() =>
                exportarCsv(
                  `ateste-composicao-${mes}-${natureza}.csv`,
                  [
                    'Credenciado',
                    'OS',
                    'Valor base',
                    'Desc. faltas',
                    'Glosa veíc.',
                    'Ajustes (+)',
                    'Ajustes (−)',
                    'Valor final',
                  ],
                  linhasFiltradas.map((l) => [
                    l.credenciadoNome,
                    String(l.numeroOs),
                    l.valorBase,
                    l.descontoFaltas,
                    l.glosaVeiculo,
                    l.ajustesPositivos,
                    l.ajustesNegativos,
                    l.valorFinal,
                  ]),
                )
              }
            >
              <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Exportar
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 pr-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Refinar
            </span>
            <SearchableSelect
              value={credenciadoId}
              onChange={definirCredenciadoId}
              options={opcoesCredenciado}
              placeholder="Credenciado: todos"
              searchPlaceholder="Buscar credenciado…"
              triggerClassName="h-9 w-[220px]"
              clearable
              ariaLabel="Filtrar por credenciado"
            />
            <SearchableSelect
              value={dreId}
              onChange={definirDreId}
              options={opcoesDre}
              placeholder="DRE: todas"
              searchPlaceholder="Buscar DRE…"
              triggerClassName="h-9 w-[180px]"
              clearable
              ariaLabel="Filtrar por DRE"
            />
            <SearchableSelect
              value={osId}
              onChange={definirOsId}
              options={opcoesOs}
              placeholder="OS: todas"
              searchPlaceholder="Buscar OS…"
              triggerClassName="h-9 w-[160px]"
              clearable
              ariaLabel="Filtrar por OS"
            />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs tabular-nums text-muted-foreground">
                {credenciados.length} credenciado{credenciados.length === 1 ? '' : 's'}
              </span>
              {hasFiltros && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={limparFiltros}
                  className="h-8 text-muted-foreground hover:text-foreground"
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {composicao.isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void composicao.refetch() }}
        />
      ) : credenciados.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <EmptyState
              title="Nenhum credenciado nesta composição"
              description="Ajuste os filtros para ver os valores compostos deste mês."
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40">
                <tr className="text-left">
                  <th className="w-8 px-3 py-2" />
                  <ThComposicao>Credenciado / OS</ThComposicao>
                  <ThComposicao alinhar="right">Valor base</ThComposicao>
                  <ThComposicao alinhar="right">Desc. faltas</ThComposicao>
                  <ThComposicao alinhar="right">Glosa veíc.</ThComposicao>
                  <ThComposicao alinhar="right">Ajustes (+)</ThComposicao>
                  <ThComposicao alinhar="right">Ajustes (−)</ThComposicao>
                  <ThComposicao alinhar="right" className="bg-success text-success-foreground">
                    Valor final
                  </ThComposicao>
                </tr>
              </thead>
              <tbody>
                {credenciados.map((c) => {
                  const aberto = expandidos.has(c.credenciadoId);
                  return (
                    <>
                      <tr
                        key={c.credenciadoId}
                        className="cursor-pointer border-b hover:bg-muted/30"
                        onClick={() => alternarExpandido(c.credenciadoId)}
                      >
                        <td className="px-2 py-3">
                          {aberto ? (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-medium">{c.credenciadoNome}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.oss.length} OS{c.oss.length === 1 ? '' : 's'}
                          </div>
                        </td>
                        <TdComposicaoNum valor={c.valorBase} />
                        <TdComposicaoNum
                          valor={c.descontoFaltas}
                          tom={c.descontoFaltas !== 0 ? 'negativo' : undefined}
                        />
                        <TdComposicaoNum
                          valor={c.glosaVeiculo}
                          tom={c.glosaVeiculo !== 0 ? 'negativo' : undefined}
                        />
                        <TdComposicaoNum
                          valor={c.ajustesPositivos}
                          tom={c.ajustesPositivos !== 0 ? 'positivo' : undefined}
                        />
                        <TdComposicaoNum
                          valor={c.ajustesNegativos}
                          tom={c.ajustesNegativos !== 0 ? 'negativo' : undefined}
                        />
                        <td className="bg-success/15 px-3 py-3 text-right font-bold tabular-nums text-success">
                          <MoneyDisplay value={c.valorFinal} />
                        </td>
                      </tr>
                      {aberto &&
                        c.oss.map((os) => (
                          <tr key={os.ordemServicoId} className="border-b bg-muted/10">
                            <td />
                            <td className="px-3 py-2 pl-9 text-xs text-muted-foreground">
                              OS {os.numeroOs}
                            </td>
                            <TdComposicaoNum valor={parseFloat(os.valorBase)} pequeno />
                            <TdComposicaoNum
                              valor={parseFloat(os.descontoFaltas)}
                              tom={parseFloat(os.descontoFaltas) !== 0 ? 'negativo' : undefined}
                              pequeno
                            />
                            <TdComposicaoNum
                              valor={parseFloat(os.glosaVeiculo)}
                              tom={parseFloat(os.glosaVeiculo) !== 0 ? 'negativo' : undefined}
                              pequeno
                            />
                            <TdComposicaoNum
                              valor={parseFloat(os.ajustesPositivos)}
                              tom={parseFloat(os.ajustesPositivos) !== 0 ? 'positivo' : undefined}
                              pequeno
                            />
                            <TdComposicaoNum
                              valor={parseFloat(os.ajustesNegativos)}
                              tom={parseFloat(os.ajustesNegativos) !== 0 ? 'negativo' : undefined}
                              pequeno
                            />
                            <td className="bg-success/10 px-3 py-2 text-right text-xs font-semibold tabular-nums text-success">
                              <MoneyDisplay value={os.valorFinal} />
                            </td>
                          </tr>
                        ))}
                    </>
                  );
                })}
              </tbody>
              <tfoot className="border-t bg-muted/60 font-semibold">
                <tr>
                  <td />
                  <td className="px-3 py-3">Total ({credenciados.length})</td>
                  <TdComposicaoNum valor={totais.valorBase} />
                  <TdComposicaoNum valor={totais.descontoFaltas} tom="negativo" />
                  <TdComposicaoNum valor={totais.glosaVeiculo} tom="negativo" />
                  <TdComposicaoNum valor={totais.ajustesPositivos} tom="positivo" />
                  <TdComposicaoNum valor={totais.ajustesNegativos} tom="negativo" />
                  <td className="bg-success px-3 py-3 text-right text-base text-success-foreground">
                    <MoneyDisplay value={totais.valorFinal} className="font-bold" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      <ResponsiveModal
        open={devolvendo}
        onOpenChange={definirDevolvendo}
        title="Devolver ao Operador DTP?"
        description='O ateste volta para "Congelado" e o Operador precisa reatestar. Informe o motivo para registro.'
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="motivo-devolucao">Motivo</Label>
            <Textarea
              id="motivo-devolucao"
              value={motivo}
              onChange={(e) => definirMotivo(e.target.value)}
              placeholder="Ex.: divergência no valor de uma OS específica"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirDevolvendo(false)}>
              Cancelar
            </Button>
            <Button
              disabled={motivo.trim().length === 0}
              onClick={() =>
                transicao.mutate(
                  { acao: 'DEVOLVER', motivo },
                  { onSuccess: () => definirDevolvendo(false) },
                )
              }
            >
              Devolver
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        open={liberando}
        onOpenChange={definirLiberando}
        title="Liberar o mês?"
        description="Ao liberar, a grade de Apontamentos volta a ficar editável e todos os carimbos deste ateste são descartados. Informe o motivo para registro."
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="motivo-liberacao">Motivo</Label>
            <Textarea
              id="motivo-liberacao"
              value={motivoLiberacao}
              onChange={(e) => definirMotivoLiberacao(e.target.value)}
              placeholder="Ex.: nova carga de apontamentos recebida da SME"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirLiberando(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={motivoLiberacao.trim().length === 0}
              onClick={() =>
                transicao.mutate(
                  { acao: 'LIBERAR', motivo: motivoLiberacao },
                  {
                    onSuccess: () => {
                      definirLiberando(false);
                      definirMotivoLiberacao('');
                    },
                  },
                )
              }
            >
              Liberar mês
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

function ThComposicao({
  children,
  alinhar,
  className,
}: {
  children: React.ReactNode;
  alinhar?: 'right';
  className?: string;
}) {
  return (
    <th
      className={cn(
        'px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        alinhar === 'right' && 'text-right',
        className,
      )}
    >
      {children}
    </th>
  );
}

function TdComposicaoNum({
  valor,
  tom,
  pequeno,
}: {
  valor: number;
  tom?: 'positivo' | 'negativo';
  pequeno?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-3 text-right tabular-nums',
        pequeno ? 'py-2 text-xs' : 'py-3',
        tom === 'positivo' && 'text-success',
        tom === 'negativo' && 'text-destructive',
      )}
    >
      <MoneyDisplay value={valor} />
    </td>
  );
}

function AbaConciliacaoAtestes({ mes, natureza }: { mes: string; natureza: 'PF' | 'PJ' }) {
  const { data, isLoading, isError, refetch } = useLotesAteste(mes, natureza);
  const importar = useImportarPlanilhaAteste();

  const colunas: DataTableColumn<LoteAtesteImportado & { id: string }>[] = [
    { header: 'OS', accessorKey: 'numeroOs' },
    { header: 'Arquivo', accessorKey: 'arquivoNome' },
    { header: 'CRMC', accessorKey: 'crmc' },
    { header: 'Placa', accessorKey: 'placa' },
    { header: 'Empresa', accessorKey: 'empresa' },
    { header: 'DRE', accessorKey: 'dreId' },
    {
      header: 'Dias válidos',
      cell: (l) => `${l.diasValidos}/${l.diasTotal}`,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Inconsistências',
      cell: (l) =>
        l.temInconsistencia ? (
          <Badge variant="destructive">{l.quantidadeInconsistencias}</Badge>
        ) : (
          <Badge variant="outline">Sem inconsistências</Badge>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => {
            const arquivo = new File([], 'planilha.xlsx');
            importar.mutate(arquivo);
          }}
          disabled={importar.isPending}
        >
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          {importar.isPending ? 'Importando…' : 'Importar planilha PMSP'}
        </Button>
      </div>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={(data ?? []).map((l) => ({ ...l, id: l.id }))}
          isLoading={isLoading}
          emptyTitle="Nenhuma planilha importada"
          emptyDescription="Importe a planilha PMSP para conciliar os apontamentos com o ateste."
          ariaLabel="Conciliação de atestes"
        />
      )}
    </div>
  );
}

function AbaPagamentoSelamento({ mes }: { mes: string }) {
  const pipeline = useCicloFinanceiroPipeline(mes);

  return (
    <div className="space-y-4">
      {pipeline.data && (
        <PipelineFinanceiro
          dados={pipeline.data}
          titulo="Ciclo orçamentário-financeiro desta competência"
        />
      )}

      <Accordion type="single" collapsible defaultValue="sof">
        <AccordionItem value="sof">
          <AccordionTrigger>
            Exportação SOF (lotes e arquivos){' '}
            <span className="font-normal text-muted-foreground">
              · Gerar lote, marcar envio/confirmação, anexar protocolo
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="flex items-center justify-between p-4">
                <p className="text-sm text-muted-foreground">
                  Os lotes de empenho e liquidação enviados ao SOF ficam na tela de Lotes SOF.
                </p>
                <Button asChild variant="outline">
                  <Link to="/operacao/financeiro/sof">
                    Abrir Lotes SOF <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="status">
          <AccordionTrigger>
            Pagamento & Conciliação{' '}
            <span className="font-normal text-muted-foreground">
              · Retorno SOF e conciliação bancária
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="flex items-center justify-between p-4">
                <p className="text-sm text-muted-foreground">
                  Status do ciclo empenho → liquidação → pagamento fica na tela de Status SOF.
                </p>
                <Button asChild variant="outline">
                  <Link to="/operacao/financeiro/sof/status">
                    Abrir Status SOF <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="selamento">
          <AccordionTrigger>
            Selamento Contábil{' '}
            <span className="font-normal text-muted-foreground">
              · Bloqueio definitivo da competência
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <EmptyState
              title="Selamento Contábil"
              description="Trava definitiva da competência mensal já fechada — etapa futura da migração."
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export default FechamentoMensalV2DetailPage;
